"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useTheme } from "next-themes";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronRight, Sun, Moon } from "lucide-react";
import { cn } from "@/lib/utils";

const LIME = "#ccfc45";

const navLinks = [
    { name: "Home", href: "/" },
    { name: "Marketplace", href: "/extensions" },
    { name: "Pricing", href: "/pricing" },
    { name: "Blog", href: "/blog" },
    { name: "Support", href: "/support" },
];

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [mounted, setMounted] = useState(false);
    const pathname = usePathname();
    const { theme, setTheme, resolvedTheme } = useTheme();

    useEffect(() => {
        setMounted(true);
        const handleScroll = () => setScrolled(window.scrollY > 30);
        window.addEventListener("scroll", handleScroll);

        if (mobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }

        return () => {
            window.removeEventListener("scroll", handleScroll);
            document.body.style.overflow = 'auto';
        };
    }, [mobileMenuOpen]);

    if (pathname === "/login" || pathname === "/signup") return null;

    const isDark = mounted ? (resolvedTheme === "dark") : true;

    // Adapt border / bg based on theme
    const scrolledBg = "bg-background/90";
    const defaultBg = "bg-background/40";
    const borderColor = "border-border";
    const textColor = "text-foreground";
    const mutedText = "text-muted-foreground";
    const pillBg = "bg-muted";
    const pillBorder = "border-border-subtle";
    const btnBorder = "border-border hover:border-primary/50";

    return (
        <>
            <motion.nav
                initial={{ y: -80, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className={cn(
                    "fixed top-0 left-1/2 -translate-x-1/2 z-50 transition-all duration-500",
                    "rounded-b-[2rem] md:rounded-b-[2.5rem]",
                    "w-[90%] md:w-auto md:min-w-[700px] lg:min-w-[900px]",
                    borderColor,
                    scrolled
                        ? `${scrolledBg} backdrop-blur-3xl border-x border-b shadow-[0_15px_40px_rgba(0,0,0,0.15)] py-2 px-5 md:px-6`
                        : `${defaultBg} backdrop-blur-2xl border-x border-b py-3 px-6 md:px-8 border-white/5 shadow-sm`
                )}
            >
                <div className="flex items-center justify-between gap-4 md:gap-8">
                    {/* Logo */}
                    <Link href="/" className="flex items-center gap-2 group shrink-0">
                        <div className="relative w-7 h-7 md:w-8 md:h-8 rounded-full overflow-hidden flex items-center justify-center border border-border-subtle shadow-sm transition-transform group-hover:scale-105">
                            <Image
                                src="/brand-icon.png"
                                alt="Restaubee Logo"
                                width={32}
                                height={32}
                                className="object-cover"
                            />
                        </div>
                        <span className={cn("font-black text-base md:text-lg tracking-tighter", textColor)}>
                            Restau<span style={{ color: LIME }}>bee</span>
                        </span>
                    </Link>

                    {/* Desktop nav pills */}
                    <div className={cn("hidden md:flex items-center gap-0.5 p-1 rounded-full border", pillBg, pillBorder)}>
                        {navLinks.map((link) => {
                            const isActive = pathname === link.href;
                            return (
                                <Link
                                    key={link.name}
                                    href={link.href}
                                    className={cn(
                                        "relative px-4 py-1 rounded-full text-[13px] font-bold transition-all duration-300",
                                        isActive ? "text-black/80" : cn(mutedText, "hover:text-foreground")
                                    )}
                                >
                                    {isActive && (
                                        <motion.div
                                            layoutId="nav-active"
                                            className="absolute inset-0 rounded-full"
                                            style={{ background: LIME }}
                                            transition={{ type: "spring", bounce: 0.2, duration: 0.55 }}
                                        />
                                    )}
                                    <span className="relative z-10">{link.name}</span>
                                </Link>
                            );
                        })}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                        {/* Theme Toggle */}
                        {mounted && (
                            <button
                                onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}
                                className={cn(
                                    "p-2.5 rounded-xl border transition-all duration-300",
                                    btnBorder, textColor
                                )}
                                aria-label="Toggle theme"
                            >
                                {resolvedTheme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
                            </button>
                        )}

                        <Link href="/login" className="hidden md:block">
                            <motion.button
                                whileHover={{ scale: 1.04 }}
                                whileTap={{ scale: 0.96 }}
                                className={cn(
                                    "relative px-5 py-1.5 rounded-full text-[12px] font-bold border transition-all overflow-hidden",
                                    textColor, btnBorder,
                                    "bg-surface hover:bg-muted"
                                )}
                            >
                                Login
                            </motion.button>
                        </Link>
                        <Link href="/signup" className="hidden md:block">
                            <motion.button
                                whileHover={{ scale: 1.04 }}
                                whileTap={{ scale: 0.96 }}
                                className="group relative px-6 py-1.5 rounded-full text-[12px] font-black text-black overflow-hidden transition-all shadow-[0_4px_15px_rgba(204,252,69,0.2)] whitespace-nowrap"
                                style={{ background: LIME }}
                            >
                                <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity" />
                                <span className="relative z-10">Get Started</span>
                            </motion.button>
                        </Link>
 
                        {/* Mobile toggle */}
                        <button
                            className={cn("md:hidden p-2 rounded-full border", textColor, btnBorder)}
                            onClick={() => setMobileMenuOpen(true)}
                            aria-label="Open navigation menu"
                        >
                            <Menu size={18} />
                        </button>
                    </div>
                </div>
            </motion.nav>

            {/* Mobile menu */}
            <AnimatePresence>
                {mobileMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0, x: "100%" }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: "100%" }}
                        transition={{ type: "spring", damping: 30, stiffness: 300 }}
                        className={cn(
                            "fixed inset-0 z-[60] flex flex-col p-8",
                            "bg-background"
                        )}
                    >
                        <div className="flex items-center justify-between mb-14">
                            <span className={cn("font-black text-2xl tracking-tighter", textColor)}>
                                Restau<span style={{ color: LIME }}>bee</span>
                            </span>
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={() => setMobileMenuOpen(false)}
                                    className={cn("p-2 rounded-full border", textColor, btnBorder)}
                                    aria-label="Close navigation menu"
                                >
                                    <X size={20} />
                                </button>
                            </div>
                        </div>
 
                        <div className="flex flex-col flex-1">
                            {navLinks.map((link, i) => (
                                <Link key={link.name} href={link.href} onClick={() => setMobileMenuOpen(false)}>
                                    <motion.div
                                        initial={{ x: -30, opacity: 0 }}
                                        animate={{ x: 0, opacity: 1 }}
                                        transition={{ delay: i * 0.07 }}
                                        className={cn(
                                            "text-4xl font-black tracking-tighter border-b py-5 flex items-center justify-between",
                                            textColor,
                                            "border-border-subtle"
                                        )}
                                    >
                                        {link.name}
                                        <ChevronRight size={20} className={mutedText} />
                                    </motion.div>
                                </Link>
                            ))}
                        </div>

                        <div className="flex flex-col gap-4 mt-8">
                            <Link href="/login" onClick={() => setMobileMenuOpen(false)}>
                                <button className={cn("w-full py-4 rounded-xl font-bold border text-center", textColor, btnBorder)}>
                                    Login
                                </button>
                            </Link>
                            <Link href="/signup" onClick={() => setMobileMenuOpen(false)}>
                                <button
                                    className="w-full py-4 rounded-xl font-black text-black text-center"
                                    style={{ background: LIME }}
                                >
                                    Get Started Free
                                </button>
                            </Link>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};
